/**
 * FloatingOutline Component
 * Ghost-like floating panel on right edge showing document outline
 * Appears on hover or scroll, with minimal styling
 */

import { useState, useEffect, useRef } from 'react';
import { List } from 'lucide-react';

export interface OutlineItem {
  id: string;
  level: number; // 1, 2, 3 for h1, h2, h3
  text: string;
}

interface FloatingOutlineProps {
  items: OutlineItem[];
  onItemClick: (id: string) => void;
  activeItemId?: string;
  className?: string;
}

export function FloatingOutline({
  items,
  onItemClick,
  activeItemId,
  className = '',
}: FloatingOutlineProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Show on hover with slight delay for smooth UX
  const handleMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsHovering(true);
    setIsVisible(true);
  };

  const handleMouseLeave = () => {
    setIsHovering(false);
    timeoutRef.current = setTimeout(() => {
      if (!isHovering) {
        setIsVisible(false);
      }
    }, 300);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Show outline when scrolling (debounced)
  useEffect(() => {
    let scrollTimeout: NodeJS.Timeout;

    const handleScroll = () => {
      setIsVisible(true);

      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        if (!isHovering) {
          setIsVisible(false);
        }
      }, 1500);
    };

    window.addEventListener('scroll', handleScroll, true);
    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      clearTimeout(scrollTimeout);
    };
  }, [isHovering]);

  // Get indent class based on level
  const getIndentClass = (level: number) => {
    switch (level) {
      case 1:
        return 'pl-0';
      case 2:
        return 'pl-3';
      case 3:
        return 'pl-6';
      default:
        return 'pl-0';
    }
  };

  // Get text size class based on level
  const getTextClass = (level: number) => {
    switch (level) {
      case 1:
        return 'text-xs font-medium text-stone-700';
      case 2:
        return 'text-xs text-stone-600';
      case 3:
        return 'text-xs text-stone-500';
      default:
        return 'text-xs text-stone-600';
    }
  };

  if (items.length === 0) {
    return null;
  }

  return (
    <>
      {/* Hover trigger zone - always visible on right edge */}
      <div
        ref={containerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className={`fixed right-0 top-1/2 -translate-y-1/2 z-30 ${className}`}
      >
        {/* Trigger tab */}
        <div
          className={`
            flex items-center justify-center w-6 h-12 rounded-l-lg
            transition-all duration-200 cursor-pointer
            ${isVisible
              ? 'bg-stone-200/80 text-stone-600'
              : 'bg-stone-100/60 text-stone-400 hover:bg-stone-200/70 hover:text-stone-500'
            }
          `}
        >
          <List size={14} />
        </div>

        {/* Floating panel */}
        <div
          className={`
            absolute right-6 top-1/2 -translate-y-1/2
            w-56 max-h-80 overflow-y-auto
            bg-white/95 backdrop-blur-sm rounded-lg
            border border-stone-200/80 shadow-lg shadow-stone-200/50
            transition-all duration-200 origin-right
            ${isVisible
              ? 'opacity-100 scale-100 translate-x-0'
              : 'opacity-0 scale-95 translate-x-2 pointer-events-none'
            }
          `}
        >
          <div className="px-3 py-2 border-b border-stone-100">
            <span className="text-xs font-medium text-stone-500 uppercase tracking-wide">
              Outline
            </span>
          </div>

          <nav className="py-2">
            {items.map((item) => (
              <button
                key={item.id}
                onClick={() => onItemClick(item.id)}
                className={`
                  w-full text-left px-3 py-1.5 transition-colors
                  hover:bg-stone-50
                  ${getIndentClass(item.level)}
                  ${getTextClass(item.level)}
                  ${activeItemId === item.id
                    ? 'bg-coral-50 text-coral-700 border-l-2 border-coral-400'
                    : 'border-l-2 border-transparent'
                  }
                `}
              >
                <span className="line-clamp-1">{item.text}</span>
              </button>
            ))}
          </nav>
        </div>
      </div>
    </>
  );
}

export default FloatingOutline;
